import type { ActivityEntry, ActivityDiff, UserRef } from '@mas-flow/types';

export interface FormattedActivity {
  verb: string;
  detail?: string;
}

const SCORE_LABELS: Record<string, string> = {
  businessValue: 'BV',
  timeCriticality: 'TC',
  riskOpportunity: 'RR',
  jobSize: 'Size',
};

function formatScoreDiff(diff?: ActivityDiff): string {
  if (!diff) return 'changed scores';
  const parts: string[] = [];
  for (const [k, v] of Object.entries(diff)) {
    const label = SCORE_LABELS[k];
    if (!label) continue;
    parts.push(`${label} ${String(v.from ?? '—')} → ${String(v.to ?? '—')}`);
  }
  return parts.length > 0 ? `scored ${parts.join(', ')}` : 'changed scores';
}

function formatVoteDiff(diff?: import('@mas-flow/types').ActivityDiff): string {
  if (!diff) return 'voted (no changes)';
  const parts: string[] = [];
  const SCORE_PATH_LABELS: Record<string, string> = {
    'scores.businessValue': 'BV',
    'scores.timeCriticality': 'TC',
    'scores.riskOpportunity': 'RR',
    'scores.jobSize': 'Size',
  };
  for (const [k, v] of Object.entries(diff)) {
    const label = SCORE_PATH_LABELS[k];
    if (!label) continue;
    parts.push(`${label} ${String(v.from ?? '—')} → ${String(v.to ?? '—')}`);
  }
  if ('confidence' in diff) {
    parts.push(`confidence ${String(diff['confidence']?.from ?? '—')} → ${String(diff['confidence']?.to ?? '—')}`);
  }
  return parts.length > 0 ? `voted ${parts.join(', ')}` : 'voted';
}

function ownerName(diff?: ActivityDiff): string {
  const owner = diff?.['owner']?.to as UserRef | null | undefined;
  return owner?.displayName ?? '—';
}

export function formatEntry(entry: ActivityEntry): FormattedActivity {
  switch (entry.kind) {
    case 'created':
      return { verb: 'created the item' };
    case 'intakeEdited':
      return { verb: 'edited intake fields', detail: Object.keys(entry.diff ?? {}).join(', ') };
    case 'scoreChanged':
      return { verb: formatScoreDiff(entry.diff) };
    case 'confidenceChanged':
      return { verb: `set confidence to ${String(entry.diff?.['confidence']?.to ?? '—')}` };
    case 'lifecycleChanged':
      return { verb: `moved to ${String(entry.diff?.['lifecycle']?.to ?? '—')}` };
    case 'decisionChanged':
      return { verb: `set decision to ${String(entry.diff?.['decision']?.to ?? '—')}` };
    case 'anchorFlagged': {
      const anchorDetail = entry.diff?.['anchorWhy']?.to as string | undefined;
      return anchorDetail ? { verb: 'flagged as anchor', detail: anchorDetail } : { verb: 'flagged as anchor' };
    }
    case 'anchorUnflagged':
      return { verb: 'removed anchor flag' };
    case 'owned':
      return { verb: `assigned owner to ${ownerName(entry.diff)}` };
    case 'archived':
      return { verb: 'archived' };
    case 'restored':
      return { verb: 'restored' };
    case 'voteCast':
      return { verb: formatVoteDiff(entry.diff) };
    case 'voteWithdrawn':
      return { verb: 'withdrew vote' };
    case 'commented':
      return entry.comment ? { verb: 'commented', detail: entry.comment } : { verb: 'commented' };
  }
}
